/** Pipeline milestones surfaced to Studio (ingest) and Tuner (egress) UIs. */
export type WebRtcConnectionStep =
  | "mic"
  | "peer"
  | "offer"
  | "session"
  | "publish"
  | "subscribe"
  | "renegotiate"
  | "register"
  | "stream"
  | "connected";

/**
 * Superset of the ingest milestones, in the order they can occur. Going live
 * over a warm connection skips `peer`/`offer`; a cold connect skips `publish`.
 * Either way the emitted steps advance monotonically through this list, which
 * is what the progress UI indexes against.
 */
export const INGEST_STEPS: readonly WebRtcConnectionStep[] = [
  "mic",
  "peer",
  "offer",
  "publish",
  "register",
  "stream",
  "connected",
];

export const EGRESS_STEPS: readonly WebRtcConnectionStep[] = [
  "peer",
  "subscribe",
  "renegotiate",
  "stream",
  "connected",
];

export function ingestStepIndex(step: WebRtcConnectionStep): number {
  return INGEST_STEPS.indexOf(step);
}

export function egressStepIndex(step: WebRtcConnectionStep): number {
  return EGRESS_STEPS.indexOf(step);
}

function isIceConnected(state: RTCIceConnectionState): boolean {
  return state === "connected" || state === "completed";
}

/** Wait until ICE is connected (Cloudflare echo pattern; preferred for Safari egress). */
export function waitForIceConnected(
  peerConnection: RTCPeerConnection,
  options?: { timeoutMs?: number },
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 20_000;

  if (isIceConnected(peerConnection.iceConnectionState)) {
    return Promise.resolve();
  }
  if (peerConnection.iceConnectionState === "failed") {
    return Promise.reject(new Error("WebRTC ICE connection failed"));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("WebRTC ICE connection timed out"));
    }, timeoutMs);

    const onState = (): void => {
      const state = peerConnection.iceConnectionState;
      if (isIceConnected(state)) {
        cleanup();
        resolve();
      } else if (state === "failed" || state === "closed") {
        cleanup();
        reject(new Error("WebRTC ICE connection failed"));
      }
    };

    const cleanup = (): void => {
      clearTimeout(timeout);
      peerConnection.removeEventListener("iceconnectionstatechange", onState);
    };

    peerConnection.addEventListener("iceconnectionstatechange", onState);
  });
}

/** Wait until the peer connection is up or failed (used before clearing "connecting" UI). */
export function waitForPeerConnected(
  peerConnection: RTCPeerConnection,
  options?: { timeoutMs?: number },
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? 20_000;

  if (peerConnection.connectionState === "connected") {
    return Promise.resolve();
  }
  if (peerConnection.connectionState === "failed") {
    return Promise.reject(new Error("WebRTC connection failed"));
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("WebRTC connection timed out"));
    }, timeoutMs);

    const onState = (): void => {
      const state = peerConnection.connectionState;
      if (state === "connected") {
        cleanup();
        resolve();
      } else if (state === "failed" || state === "closed") {
        cleanup();
        reject(new Error("WebRTC connection failed"));
      }
    };

    const cleanup = (): void => {
      clearTimeout(timeout);
      peerConnection.removeEventListener("connectionstatechange", onState);
    };

    peerConnection.addEventListener("connectionstatechange", onState);
  });
}

/** Advance through ingest steps for demo mode (no real PeerConnection). */
export async function simulateIngestSteps(
  onStep: (step: WebRtcConnectionStep) => void,
  delayMs = 400,
): Promise<void> {
  for (const step of INGEST_STEPS) {
    onStep(step);
    if (step !== "connected") await new Promise((r) => setTimeout(r, delayMs));
  }
}

/** Advance through egress steps for demo mode. */
export async function simulateEgressSteps(
  onStep: (step: WebRtcConnectionStep) => void,
  delayMs = 400,
): Promise<void> {
  for (const step of EGRESS_STEPS) {
    if (step === "renegotiate") continue;
    onStep(step);
    if (step !== "connected") await new Promise((r) => setTimeout(r, delayMs));
  }
}
